
import { connect } from 'react-redux';
import { addTrackToSavedList, removeTrackFromSavedList, saveTrack } from '../redux/actions/userActions';
import { forwardRef, useEffect, useRef, useState } from 'react'
import { getAnalytics, logEvent } from "firebase/analytics";

import Modal from '@mui/material/Modal';
import { Check } from '@mui/icons-material';
import { SvgIcon, SwipeableDrawer } from '@mui/material';
import { Bookmark, BookmarkBorder, DeleteOutline } from '@mui/icons-material';

import EmojiPickerModal from './EmojiPickerModal';
import NewSavedListButton from './NewSavedListButton';
import SigninButtonPopup from './SigninButtonPopup'
import { TIP_SAVED_LIST_PICKER_LEARNED_V1 } from '../constants/local_storage_constants';

const savedListPickerTipWasLearned = () => {
    try {
        return window.localStorage.getItem(TIP_SAVED_LIST_PICKER_LEARNED_V1) === 'true';
    } catch (_) {
        return false;
    }
}

const persistSavedListPickerTipLearned = () => {
    try {
        window.localStorage.setItem(TIP_SAVED_LIST_PICKER_LEARNED_V1, 'true');
    } catch (_) {
        // Keep the learned state in memory when browser storage is unavailable.
    }
}

const savedListOrderAtOpening = (savedLists, selectedListIds) => [
    ...savedLists.filter((savedList) => selectedListIds.has(savedList.id)),
    ...savedLists.filter((savedList) => !selectedListIds.has(savedList.id)),
];

const syncSavedListOrder = (
    currentOrder,
    latestSavedLists,
    selectedListIds,
    pendingNewListNames,
) => {
    if (currentOrder.length === 0) {
        return savedListOrderAtOpening(latestSavedLists, selectedListIds);
    }

    const latestById = new Map(latestSavedLists.map((savedList) => [savedList.id, savedList]));
    const currentIds = new Set(currentOrder.map((savedList) => savedList.id));
    const retainedLists = currentOrder
        .filter((savedList) => latestById.has(savedList.id))
        .map((savedList) => latestById.get(savedList.id));
    const newLists = latestSavedLists.filter((savedList) => !currentIds.has(savedList.id));
    const pendingNewLists = pendingNewListNames
        .map((pendingName) => newLists.find((savedList) => savedList.name === pendingName))
        .filter(Boolean);
    const pendingNames = new Set(pendingNewLists.map((savedList) => savedList.name));

    return [
        ...pendingNewLists,
        ...retainedLists,
        ...newLists.filter((savedList) => !pendingNames.has(savedList.name)),
    ];
};

const SigninButtonPopupForwardRef = forwardRef((props, ref) => (
    <SigninButtonPopup {...props} />
));


export const BookmarkButton = ({
    buttonClassName,
    editMode,
    iconClassName,
    iconStyle,
    renderMode,
    trackId,
    source,

    // Redux.
    authenticated,
    addTrackToSavedList,
    removeTrackFromSavedList,
    savedLists,
    savedTrackIdToSavedListIds,
    saveTrack,
}) => {

    const analytics = getAnalytics();

    const isTrackSaved = () => {
        return savedTrackIdToSavedListIds[trackId] !== undefined;
    }

    const isTrackSavedToList = (listId) => {
        return savedTrackIdToSavedListIds[trackId]?.includes(listId);
    }

    const saved = isTrackSaved();

    const [openListPicker, setOpenListPicker] = useState(false);
    const [showSavedListPickerTip, setShowSavedListPickerTip] = useState(false);
    const [openSigninButtonPopup, setOpenSigninButtonPopup] = useState(false);

    const [showUpdatedMessage, setShowUpdatedMessage] = useState(null);
    const [showSaveToListEmojiPicker, setShowSaveToListEmojiPicker] = useState(false);
    const [orderedSavedLists, setOrderedSavedLists] = useState([]);
    const savedListPickerTipLearnedRef = useRef(savedListPickerTipWasLearned());
    const pendingNewListNamesRef = useRef([]);

    const toggleSigninButtonPopup = (open) => (event) => {
        if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setOpenSigninButtonPopup(open);
    }

    const saveTrackClick = () => {
        if (!authenticated) {
            setOpenSigninButtonPopup(true);
            return;
        }

        const tipWasLearned =
            savedListPickerTipLearnedRef.current || savedListPickerTipWasLearned();
        savedListPickerTipLearnedRef.current = tipWasLearned;
        setShowSavedListPickerTip(!tipWasLearned);
        pendingNewListNamesRef.current = [];
        setOrderedSavedLists(savedListOrderAtOpening(
            savedLists,
            new Set(savedTrackIdToSavedListIds[trackId] || []),
        ));
        setOpenListPicker(true);

        if (!saved) {
            saveTrack(trackId, !saved);
            logEvent(analytics, 'bookmark_true');
        }
    }

    const unsaveClick = () => {
        if (!authenticated) {
            setOpenSigninButtonPopup(true);
            return;
        }
        saveTrack(trackId, false);
        logEvent(analytics, 'bookmark_false');
    }

    const toggleSave = () => {
        if (!authenticated) {
            setOpenSigninButtonPopup(true);
            return;
        }

        if (saved) {
            setOpenListPicker(false);
            saveTrack(trackId, false);
            logEvent(analytics, 'bookmark_false');
        } else {
            saveTrack(trackId, true);
            logEvent(analytics, 'bookmark_true');
        }
    }

    const icon = saved ? <Bookmark style={iconStyle} /> : <BookmarkBorder style={iconStyle} />;

    const finishSavedListMutation = async (mutationResult, successMessage) => {
        try {
            const mutationSucceeded = await mutationResult;
            if (mutationSucceeded === true) {
                savedListPickerTipLearnedRef.current = true;
                persistSavedListPickerTipLearned();
                setShowSavedListPickerTip(false);
                setShowUpdatedMessage(successMessage);
            }
        } catch (_) {
            // Leave the instruction unlearned so it appears on the next opening.
        }
    }

    const handleSavedListClick = (list) => {
        if (isTrackSavedToList(list.id)) {
            const mutationResult = removeTrackFromSavedList(trackId, list.name, list.id);
            finishSavedListMutation(mutationResult, `${list.name} Removed from list`);
        } else {
            const mutationResult = addTrackToSavedList(trackId, list.name, list.id);
            finishSavedListMutation(mutationResult, `${list.name} Added to list`);
        }
    }

    const saveToListClick = () => {
        setShowSaveToListEmojiPicker(true);
    }

    const onEmojiPickerClick = (emojiInfo) => {
        setShowSaveToListEmojiPicker(false);
        if (!savedLists.some((savedList) => savedList.name === emojiInfo.emoji)) {
            pendingNewListNamesRef.current = [
                emojiInfo.emoji,
                ...pendingNewListNamesRef.current.filter((name) => name !== emojiInfo.emoji),
            ];
        }
        const mutationResult = addTrackToSavedList(trackId, emojiInfo.emoji, null);
        finishSavedListMutation(mutationResult, `${emojiInfo.emoji} Added to list`);
    }

    useEffect(() => {
        if (!openListPicker) {
            return;
        }

        setOrderedSavedLists((currentOrder) => syncSavedListOrder(
            currentOrder,
            savedLists,
            new Set(savedTrackIdToSavedListIds[trackId] || []),
            pendingNewListNamesRef.current,
        ));
    }, [openListPicker, savedLists, savedTrackIdToSavedListIds, trackId]);

    useEffect(() => {
        if (showUpdatedMessage) {
            const timer = setTimeout(() => {
                setShowUpdatedMessage(null);
            }, 2300);
            return () => clearTimeout(timer);
        }
    }, [showUpdatedMessage]);

    return (
        <span>
            <Modal
                open={openSigninButtonPopup}
                onClose={toggleSigninButtonPopup(false)}
            >
                <SigninButtonPopupForwardRef close={toggleSigninButtonPopup(false)} />
            </Modal>
            
            <Modal
                BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0)' } }}
                open={showUpdatedMessage !== null} onClose={() => setShowUpdatedMessage(null)}>
                <div className='horizontalCenter'>
                    <button style={{ width: '100%' }} onClick={() => setShowUpdatedMessage(null)}>
                        <div style={{ margin: '20px', padding: '13px 20px', background: 'var(--app-surface)', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', textAlign: 'center', fontSize: '18px', fontWeight: '600', borderRadius: '10px' }}>
                            {showUpdatedMessage}
                        </div>
                    </button>
                </div>
            </Modal>

            <SwipeableDrawer
                PaperProps={{
                    sx: {
                        borderRadius: '18px 18px 0px 0px'
                    }
                }}
                anchor='bottom' open={openListPicker} onClose={() => { setOpenListPicker(false) }}>

                <div>
                    <div className='savedListPickerDrawer horizontalCenter appDrawerSurface'>
                        <div className='savedListPickerDragIndicator' aria-hidden='true' />
                        <div className='savedListPickerSummary'>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>
                                            <img src={source.imageURL} alt='' className="saveSourceImage podcastArtwork"></img>
                                        </td>
                                        <td style={{ width: '100%', padding: '0px 12px' }}>
                                            <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                                {saved ? 'Saved episode' : 'Save episode'}
                                            </div>
                                            <div className='saveTrackTitle'>
                                                {source.title}
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                type='button'
                                                aria-label={saved ? 'Remove episode from saved' : 'Save episode'}
                                                onClick={toggleSave}
                                            >
                                                <DeleteOutline style={{ verticalAlign: 'middle', fontSize: '24px' }} />
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className='savedListPickerBody'>
                            {orderedSavedLists.length === 0 ? (
                                <div className='savedListPickerEmptyState'>
                                    <div className='savedListPickerEmptyTitle'>No lists yet.</div>
                                    <div>Create a list to organize this episode.</div>
                                </div>
                            ) : showSavedListPickerTip && (
                                <p className='savedListPickerInstruction'>
                                    Organize your saved episodes into lists.
                                    <br />
                                    Tap an emoji to add this episode to that list.
                                </p>
                            )}

                            <div className="savedListsGrid">
                                {orderedSavedLists.map((savedList) => {
                                    const selected = isTrackSavedToList(savedList.id);
                                    return (
                                        <button
                                            aria-pressed={selected}
                                            className="savedListButton"
                                            key={savedList.id}
                                            onClick={() => handleSavedListClick(savedList)}
                                        >
                                            <div className="savedListButtonEmoji">
                                                {savedList.name}
                                                {selected && (
                                                <span className="savedListButtonCheckmark">
                                                    <SvgIcon fontSize="inherit" sx={{ stroke: "var(--app-action-ink)", strokeWidth: 0.5 }} >
                                                        <Check style={{ color: 'var(--app-action-ink)' }} />
                                                    </SvgIcon>
                                                </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                                <NewSavedListButton onClick={saveToListClick} />
                            </div>
                        </div>
                    </div>
                </div>
            </SwipeableDrawer>

            <EmojiPickerModal showEmojiPicker={showSaveToListEmojiPicker} onEmojiClick={onEmojiPickerClick} onClose={() => {setShowSaveToListEmojiPicker(false)}} />

            {renderMode === 'row' && !editMode ?
                <button
                    type='button'
                    onClick={saveTrackClick}
                    className={['previewInfoOptionsBookmarkRow', buttonClassName].filter(Boolean).join(' ')}
                >
                    <span className='previewInfoOptionsBookmarkMain'>
                        <span className='previewInfoOptionsBookmarkIcon'>
                            {icon}
                        </span>
                        <span>{saved ? 'Saved episode' : 'Save episode'}</span>
                    </span>
                </button> :
                editMode && saved ?
                <button
                    type='button'
                    aria-label='Remove episode from saved'
                    onClick={unsaveClick}
                    style={{ textAlign: 'right', width: '70px' }}
                >
                    <span style={{ verticalAlign: 'middle', height: '14px' }}>
                        <DeleteOutline style={{ fontSize: '21px', verticalAlign: 'middle' }} />
                    </span>
                </button> :

                <button
                    type='button'
                    aria-label={iconClassName ? undefined : (saved ? 'Manage saved episode' : 'Save episode')}
                    onClick={saveTrackClick}
                    className={buttonClassName}
                >
                    {iconClassName ?
                        <div>
                            <div className={iconClassName}>
                                {icon}
                            </div>
                            {saved ? 'Saved' : 'Save'}
                        </div>
                        :
                        icon
                    }
                </button>
            }
        </span >
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    savedTrackIdToSavedListIds: state.user.savedTrackIdToSavedListIds,
    savedLists: state.user.savedLists,
})

const mapActionsToProps = {
    addTrackToSavedList,
    removeTrackFromSavedList,
    saveTrack,
}

export default connect(mapStateToProps, mapActionsToProps)(BookmarkButton);
